import ConductionModal from "@/components/modals/ConductionModal";
import { AuthContext } from "@/context/AuthContext";
import ConductionAPI from "@/javascript/conduction-api";
import { Song } from "@/javascript/library-objects";
import { Utils } from "@/javascript/utils";
import {
    Badge,
    Button,
    Flex,
    Group,
    Pagination,
    ScrollArea,
    Skeleton,
    Stack,
    Table,
    Text,
    Tooltip
} from "@mantine/core";
import { IconAlertCircle, IconCheck, IconEdit, IconEyeQuestion, IconFilePlus, IconPresentation, IconTrash } from "@tabler/icons-react";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import SongClassroomAssignmentForm from "./SongClassroomAssignmentForm";
import SongCreationForm from "./SongCreationForm";
import SongDeletionForm from "./SongDeletionForm";
import SongProcessor from "./SongProcessor";
import SongUsageStatsBrowser from "./SongUsageStatsBrowser";

interface SongsBrowserProps {
    onClose: () => void;
}

const SongsBrowser: React.FC<SongsBrowserProps> = ({ onClose }) => {
    const [songs, setSongs] = useState<Song[]>([]);
    const [desiredPage, setDesiredPage] = useState<number>(1);
    const [songsPerPage, setSongsPerPage] = useState<number>(15);
    const [loading, setLoading] = useState<boolean>(true);

    const auth = useContext(AuthContext);
    if (auth === undefined) {
        throw new Error("This component must be a child of AuthContext.");
    }
    const token = auth.token;
    if (token === null) {
        return;
    }

    const height = 70; // view height (vh)
    const rowHeight = 100; // pixel estimation
    const calculateSongsPerPage = useCallback(() => {
        const containerHeight = window.innerHeight * (height / 100) - 100; // Use 60% of the viewport height minus some estimate for other elements
        setSongsPerPage(Math.floor(containerHeight / rowHeight));

        if (songs && Math.floor(containerHeight / rowHeight) * (desiredPage - 1) + 1 > songs.length) {
            setDesiredPage(1);
        }
    }, [desiredPage, window.innerHeight]);

    useEffect(() => {
        calculateSongsPerPage();
        window.addEventListener("resize", calculateSongsPerPage);
        return () => window.removeEventListener("resize", calculateSongsPerPage);
    }, [calculateSongsPerPage]);

    const handleSetGuestAvailability = (songId: string, newAvailabilityStatus: boolean) => {
        setLoading(true);
        const notificationId = Utils.displayNotification(
            "Setting Guest Availability",
            undefined,
            "blue",
            "loading"
        );

        ConductionAPI.setSongGuestAvailability(songId, newAvailabilityStatus, token)
            .then(() => {

                const newSongsList = songs.map(song => {
                    if (song.songId !== songId) return song;
                    return song.setAvailableToGuests(newAvailabilityStatus);
                });
                setSongs(newSongsList);

                Utils.updateNotification(
                    notificationId,
                    "Updated Guest Availability",
                    undefined,
                    "green",
                    <IconCheck size={16} />
                );
            })
            .catch(() => {
                Utils.updateNotification(
                    notificationId,
                    "An Error Occurred",
                    undefined,
                    "red",
                    <IconAlertCircle size={16} />,
                    true
                );
            })
            .finally(() => setLoading(false));
    }

    const fetchData = async () => {
        const notificationId = Utils.displayNotification(
            "Loading Songs",
            undefined,
            "blue",
            "loading"
        );

        ConductionAPI.getAllSongsWithStats(token)
            .then((loadedSongs: Song[] | null) => {
                if (loadedSongs) {
                    if (loadedSongs.length === 0) {
                        Utils.updateNotification(
                            notificationId,
                            "No Songs Found",
                            undefined,
                            "red",
                            <IconAlertCircle size={16} />,
                            true
                        );
                    } else {
                        const sortedSongs = loadedSongs
                            .sort((a, b) => a.uploadDate.getTime() - b.uploadDate.getTime()) // Newest first
                            .sort((a, b) => (a.processed && !b.processed ? 1 : -1)); // Prioritize processed

                        setSongs(sortedSongs);
                        Utils.updateNotification(
                            notificationId,
                            "Songs Loaded",
                            undefined,
                            "green",
                            <IconCheck size={16} />
                        );
                    }
                } else {
                    Utils.updateNotification(
                        notificationId,
                        "Error Fetching Songs",
                        "An error occurred while retrieving songs.",
                        "red",
                        <IconAlertCircle size={16} />,
                        true
                    );
                }
            })
            .catch(() => {
                Utils.updateNotification(
                    notificationId,
                    "Error Fetching Songs",
                    "An unexpected error occurred.",
                    "red",
                    <IconAlertCircle size={16} />,
                    true
                );
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchData();
    }, [token]);

    const paginatedSongs = useMemo(() => {
        const start = (desiredPage - 1) * songsPerPage;
        return songs.slice(start, start + songsPerPage);
    }, [desiredPage, songs, songsPerPage]);

    const [showSongStatsModal, setShowSongStatsModal] = useState<boolean>(false);
    const [showSongCreationModal, setShowSongCreationModal] = useState<boolean>(false);
    const [showSongProcessorFor, setShowSongProcessorFor] = useState<Song | null>(null);
    const [showSongDeleteModalFor, setShowSongDeleteModalFor] = useState<Song | null>(null);
    const [showSongAssignModalFor, setShowSongAssignModalFor] = useState<Song | null>(null);
    const renderModals = () => (
        <>
            {showSongStatsModal && (
                <SongUsageStatsBrowser
                    onClose={() => setShowSongStatsModal(false)}
                    songs={songs}
                />
            )}

            {showSongCreationModal && (
                <SongCreationForm
                    onClose={() => setShowSongCreationModal(false)}
                    onCreated={() => fetchData()}
                />
            )}

            {showSongProcessorFor && (
                <SongProcessor
                    song={showSongProcessorFor}
                    onClose={() => setShowSongProcessorFor(null)}
                    onProcessed={() => fetchData()}
                />
            )}

            {showSongDeleteModalFor && (
                <SongDeletionForm
                    song={showSongDeleteModalFor}
                    onClose={() => setShowSongDeleteModalFor(null)}
                    onDeleted={() => fetchData()}
                />
            )}

            {showSongAssignModalFor && (
                <SongClassroomAssignmentForm
                    song={showSongAssignModalFor}
                    onClose={() => setShowSongAssignModalFor(null)}
                    onAssigned={() => fetchData()}
                />
            )}
        </>
    );

    return (
        <ConductionModal
            title={"Admin Song Manager"}
            size={"90%"}
            onClose={onClose}
        >
            {renderModals()}
            <Stack
                align="center"
                justify="center"
            >

                <Group>
                    <Button
                        onClick={() => setShowSongCreationModal(true)}
                    >
                        <IconFilePlus size={16} style={{ marginRight: 8 }} />
                        Create New Song
                    </Button>

                    <Button
                        onClick={() => setShowSongStatsModal(true)}
                        disabled={loading || songs.length === 0}
                    >
                        <IconPresentation size={16} style={{ marginRight: 8 }} />
                        View Song Usage Stats
                    </Button>
                </Group>

                <Flex
                    direction={"column"}
                    align="center"
                    justify="space-between"
                    style={{
                        height: `${height}vh`,
                        width: "100%"
                    }}
                >
                    {loading ? (
                        <Skeleton height={"100%"} radius="md" />
                    ) : songs.length === 0 ? (
                        <Text>No songs found.</Text>
                    ) : (
                        <ScrollArea
                            style={{
                                width: "100%"
                            }}
                        >
                            <Table
                                striped
                                stickyHeader
                                highlightOnHover
                                withColumnBorders
                            >
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th>Name</Table.Th>
                                        <Table.Th>Uploaded</Table.Th>
                                        <Table.Th>Processed</Table.Th>
                                        <Table.Th>Available To Guests</Table.Th>
                                        <Table.Th>Genres</Table.Th>
                                        <Table.Th>Artists and Contributors</Table.Th>
                                        <Table.Th>Actions</Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {paginatedSongs.map((song) => {
                                        return (
                                            <Table.Tr key={song.songName} style={{ minHeight: rowHeight }}>
                                                <Table.Td>{song.songName}</Table.Td>
                                                <Table.Td><Badge color="blue">{formatDateString(song.uploadDate)}</Badge></Table.Td>
                                                <Table.Td>{song.processed ? <Badge color="green">Yes</Badge> : <Badge color="red">No</Badge>}</Table.Td>
                                                <Table.Td>
                                                    <Tooltip
                                                        label={"Toggle Song Availability for Guests"}
                                                        withArrow
                                                    >
                                                        <Button
                                                            size="xs"
                                                            color={song.availableToGuests ? "green" : "red"}
                                                            disabled={loading || !song.processed}
                                                            onClick={() => handleSetGuestAvailability(song.songId, !song.availableToGuests)}
                                                        >
                                                            {song.availableToGuests ? "Yes" : "No"}
                                                        </Button>
                                                    </Tooltip>
                                                </Table.Td>
                                                <Table.Td>{song.genres.map((genre) => <Badge key={genre.genreId} color="blue">{genre.genreName}</Badge>)}</Table.Td>
                                                <Table.Td>{song.artists.map((artist) => <Badge key={artist.artistId} color={artist.artistType === "Composer" ? "blue" : "yellow"}>{artist.artistName}</Badge>)}</Table.Td>
                                                <Table.Td>
                                                    <Group>
                                                        <Tooltip
                                                            label={"Process Song Data"}
                                                            withArrow
                                                        >
                                                            <Button
                                                                size="xs"
                                                                disabled={loading}
                                                                onClick={() => setShowSongProcessorFor(song)}
                                                            >
                                                                <IconEdit size={20} />
                                                            </Button>
                                                        </Tooltip>
                                                        <Tooltip
                                                            label={"Delete"}
                                                            withArrow
                                                        >
                                                            <Button
                                                                size="xs"
                                                                disabled={loading}
                                                                onClick={() => setShowSongDeleteModalFor(song)}
                                                            >
                                                                <IconTrash size={20} />
                                                            </Button>
                                                        </Tooltip>
                                                        <Tooltip
                                                            label={"Assign to Classrooms"}
                                                            withArrow
                                                        >
                                                            <Button
                                                                size="xs"
                                                                disabled={loading || !song.processed}
                                                                onClick={() => setShowSongAssignModalFor(song)}
                                                            >
                                                                <IconEyeQuestion size={20} />
                                                            </Button>
                                                        </Tooltip>
                                                    </Group>
                                                </Table.Td>
                                            </Table.Tr>
                                        )
                                    })}
                                </Table.Tbody>
                            </Table>
                        </ScrollArea>
                    )}

                    {songs && songs.length > songsPerPage && (
                        <Pagination
                            total={Math.ceil(songs.length / songsPerPage)}
                            value={desiredPage}
                            onChange={setDesiredPage}
                        />
                    )}
                </Flex>
            </Stack>
        </ConductionModal>
    );
};


function formatDateString(stringDate: Date): string {
    if (!stringDate) return "Invalid Date";
    const date = new Date(stringDate + " UTC");
    return date.toLocaleString();
}


export default SongsBrowser;
